import axios from "axios";
import config from "../config";
import { resolveAuthorizationHeader } from "./authorizationHeader";
import { renewOidcSession } from "./oidcSilentRenewal";
import { isInternalTrafficScopeReady } from "../auth/backendReadiness";
import userManager from "../auth/userManager";
import {
  getMetadata,
  saveMetadata,
  getCachedFilterOptions,
  saveCachedFilterOptions,
} from "../web/utils/offlineDB";

const api = axios.create({
  baseURL: `${config.apiUrl}`,
  withCredentials: true,
  timeout: 600000, // 10 minutes timeout
});

api.interceptors.request.use(async (req) => {
  // SECURITY: Sourced from the OIDC library's in-memory session state (see
  // ./authorizationHeader) - never from localStorage.
  const authorizationHeader = await resolveAuthorizationHeader();
  if (authorizationHeader) {
    req.headers.Authorization = authorizationHeader;
  }
  // Client-asserted bypass the backend is closing in favor of a proper
  // Internal-Traffic Scope (see ../auth/backendReadiness for the flip
  // conditions and rollout hazard).
  if (!isInternalTrafficScopeReady) {
    req.headers['X-Ensaras-Client'] = 'portal';
  }
  return req;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const user = await renewOidcSession();
        if (user?.access_token) {
          originalRequest.headers.Authorization = `Bearer ${user.access_token}`;
          return api(originalRequest);
        } else {
          try {
            await userManager.removeUser();
          } catch (removeErr) {
            // ignore
          }
        }
      } catch (renewalError) {
        // Silent renewal failed - remove stale user session from storage so
        // AuthProvider updates auth.isAuthenticated to false and OidcPrivateRoute
        // redirects the user to the sign-in page.
        console.error("OIDC silent renewal failed:", renewalError);
        try {
          await userManager.removeUser();
        } catch (removeErr) {
          // ignore
        }
      }
    }

    return Promise.reject(error);
  }
);

// Intercept get calls for metadata and filter options to support offline mode
const originalGet = api.get;
api.get = async function (url, config) {
  const isMetadata = url && url.includes("/metadata/");
  const isFilterOptions = url && url.includes("/filter-options/");

  if (isMetadata || isFilterOptions) {
    // Extract formId
    let formId = null;
    if (config && config.params && config.params.form_id) {
      formId = config.params.form_id;
    } else if (config && config.params && config.params.formId) {
      formId = config.params.formId;
    } else if (url) {
      const match = url.match(/[?&]form_id=([^&]+)/) || url.match(/[?&]formId=([^&]+)/);
      if (match) {
        formId = match[1];
      }
    }

    // Extract field parameter for filter options
    let field = null;
    if (isFilterOptions) {
      if (config && config.params && config.params.field) {
        field = config.params.field;
      } else if (url) {
        const fieldMatch = url.match(/[?&]field=([^&]+)/);
        if (fieldMatch) {
          field = fieldMatch[1];
        }
      }
    }

    const canCache = isMetadata ? !!formId : (!!formId && !!field);

    if (canCache) {
      // If we are offline, attempt fallback immediately
      if (!navigator.onLine) {
        try {
          const cached = isMetadata
            ? await getMetadata(formId)
            : await getCachedFilterOptions(formId, field);

          if (cached) {
            console.log(`[Offline API] Returning cached ${isMetadata ? "metadata" : "filter options"} for form ${formId}`);
            return {
              data: cached,
              status: 200,
              statusText: "OK",
              headers: {},
              config,
            };
          }
        } catch (err) {
          console.error("Error reading from IndexedDB cache in offline mode:", err);
        }
      }

      // If we are online, try network request first
      try {
        const response = await originalGet.call(api, url, config);

        // Save to cache on success
        if (response && response.data) {
          if (isMetadata) {
            await saveMetadata(formId, response.data);
          } else {
            await saveCachedFilterOptions(formId, field, response.data);
          }
        }
        return response;
      } catch (error) {
        // If network request failed (e.g. server error, network timeout), fallback to cache
        console.warn("Network request failed, attempting cache fallback:", error);
        try {
          const cached = isMetadata
            ? await getMetadata(formId)
            : await getCachedFilterOptions(formId, field);

          if (cached) {
            console.log(`[Offline API Fallback] Returning cached ${isMetadata ? "metadata" : "filter options"} for form ${formId}`);
            return {
              data: cached,
              status: 200,
              statusText: "OK",
              headers: {},
              config,
            };
          }
        } catch (cacheErr) {
          console.error("Error reading from cache during fallback:", cacheErr);
        }
        // If no cache or cache failed, throw original error
        throw error;
      }
    }
  }

  // For other endpoints or if formId is not found, proceed normally
  return originalGet.call(api, url, config);
};

export default api;
